/**
 * CreateProductWizard.jsx
 * 4-Step wizard for creating new products with nutrition label photo
 * Matches Customer Portal functionality
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, Sparkles, RefreshCw, Check, AlertCircle, ImageIcon, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CreateProductWizard = ({ isOpen, onClose, onProductCreated, portalToken, initialBarcode = '' }) => {
  // Step: 1=Nutrition Photo, 2=Review Values, 3=Product Photo, 4=Save
  const [step, setStep] = useState(1);
  
  // Photo states
  const [nutritionPhoto, setNutritionPhoto] = useState(null);
  const [nutritionPreview, setNutritionPreview] = useState(null);
  const [productPhoto, setProductPhoto] = useState(null);
  const [productPreview, setProductPreview] = useState(null);
  
  // Recognition state
  const [recognizing, setRecognizing] = useState(false);
  const [recognizedData, setRecognizedData] = useState(null);
  const [barcodeConflictHint, setBarcodeConflictHint] = useState('');
  const [isBarcodeLocked, setIsBarcodeLocked] = useState(Boolean((initialBarcode || '').trim()));
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    barcode: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    sugar: 0
  });
  
  const [saving, setSaving] = useState(false);
  const nutritionPhotoRef = useRef(null);
  const productPhotoRef = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialBarcode) {
      setFormData((prev) => ({ ...prev, barcode: initialBarcode }));
    }
    if (isOpen) {
      setIsBarcodeLocked(Boolean((initialBarcode || '').trim()));
      setBarcodeConflictHint('');
    }
  }, [isOpen, initialBarcode]);

  // Handle nutrition photo
  const handleNutritionPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild zu gross (max. 10MB)');
      return;
    }
    
    setNutritionPhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setNutritionPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Analyze nutrition label
  const analyzeNutritionLabel = async () => {
    if (!nutritionPhoto || !portalToken) return;
    
    setRecognizing(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const response = await axios.post(
            `${API}/portal/nutrition/recognize-label`,
            { image_data: reader.result },
            { 
              headers: { Authorization: `Bearer ${portalToken}` },
              timeout: 60000
            }
          );
          
          if (response.data) {
            setRecognizedData(response.data);
            const scannerBarcode = (initialBarcode || '').trim();
            const aiBarcode = String(response.data.barcode || '').trim();
            if (scannerBarcode && aiBarcode && scannerBarcode !== aiBarcode) {
              setBarcodeConflictHint(`Scanner-Barcode ${scannerBarcode} wurde beibehalten (OCR: ${aiBarcode}).`);
            } else {
              setBarcodeConflictHint('');
            }
            setFormData((prev) => ({
              ...prev,
              name: response.data.product_name || '',
              brand: response.data.brand || '',
              barcode: prev.barcode || scannerBarcode || aiBarcode || '',
              calories: response.data.calories_per_100g || 0,
              protein: response.data.protein_per_100g || 0,
              carbs: response.data.carbs_per_100g || 0,
              fat: response.data.fat_per_100g || 0,
              fiber: response.data.fiber_per_100g || 0,
              sugar: response.data.sugar_per_100g || 0
            }));
            toast.success('Nährwerte erkannt!');
            setStep(2);
          }
        } catch (err) {
          console.error('Recognition error:', err);
          toast.error('Erkennung fehlgeschlagen - bitte manuell eingeben');
          // Allow manual entry
          setStep(2);
        }
        setRecognizing(false);
      };
      reader.readAsDataURL(nutritionPhoto);
    } catch (err) {
      toast.error('Fehler beim Lesen des Bildes');
      setRecognizing(false);
    }
  };

  // Handle product photo
  const handleProductPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setProductPhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProductPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Save product
  const saveProduct = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte einen Produktnamen eingeben');
      return;
    }
    
    setSaving(true);
    try {
      // Create custom product
      await axios.post(
        `${API}/portal/custom-products`,
        {
          name: formData.name,
          brand: formData.brand,
          barcode: formData.barcode,
          calories_per_100g: formData.calories,
          protein_per_100g: formData.protein,
          carbs_per_100g: formData.carbs,
          fat_per_100g: formData.fat,
          fiber_per_100g: formData.fiber,
          sugar_per_100g: formData.sugar,
          image_url: productPreview
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('Produkt erstellt!');
      onProductCreated?.();
      resetAndClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Reset and close
  const resetAndClose = () => {
    setStep(1);
    setNutritionPhoto(null);
    setNutritionPreview(null);
    setProductPhoto(null);
    setProductPreview(null);
    setRecognizedData(null);
    setBarcodeConflictHint('');
    setIsBarcodeLocked(Boolean((initialBarcode || '').trim()));
    setFormData({
      name: '', brand: '', barcode: initialBarcode || '',
      calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center gap-3">
          <button onClick={resetAndClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="flex-1">
            <h2 className="text-white font-bold">Neues Produkt hinzufügen</h2>
            <p className="text-zinc-500 text-xs">Schritt {step} von 4</p>
          </div>
          <Package className="w-6 h-6 text-yellow-400" />
        </div>
        
        {/* Step Indicator */}
        <div className="flex gap-1 mt-3">
          {[1, 2, 3, 4].map(s => (
            <div 
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-yellow-500' : 'bg-zinc-700'}`}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Step 1: Nutrition Label Photo */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <h3 className="text-yellow-400 font-medium mb-2">Schritt 1: Nährwerttabelle fotografieren</h3>
              <p className="text-zinc-400 text-sm">
                Fotografiere die Nährwerttabelle auf der Verpackung. Die Werte werden automatisch erkannt.
              </p>
            </div>
            
            {!nutritionPreview ? (
              <div 
                onClick={() => nutritionPhotoRef.current?.click()}
                className="bg-zinc-800/70 rounded-2xl p-8 border-2 border-dashed border-zinc-600 cursor-pointer hover:border-yellow-500/50 transition-colors"
              >
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4">
                    <Camera className="w-10 h-10 text-yellow-400" />
                  </div>
                  <p className="text-white font-medium mb-2">Foto aufnehmen oder auswählen</p>
                  <p className="text-zinc-500 text-sm text-center">
                    Fotografiere die Nährwerttabelle auf der Produktverpackung
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <img src={nutritionPreview} alt="Nährwerttabelle" className="w-full h-48 object-cover rounded-xl" />
                  <button
                    onClick={() => { setNutritionPhoto(null); setNutritionPreview(null); }}
                    className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
                
                {!recognizing && (
                  <button
                    onClick={analyzeNutritionLabel}
                    className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Nährwerte erkennen
                  </button>
                )}
                
                {recognizing && (
                  <div className="text-center py-4">
                    <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-2" />
                    <p className="text-white">Analysiere Nährwerttabelle...</p>
                  </div>
                )}
              </div>
            )}
            
            <input
              ref={nutritionPhotoRef}
              type="file"
              accept="image/*"
              onChange={handleNutritionPhoto}
              className="hidden"
              data-testid="mobile-create-product-step1-file-input"
            />
          </div>
        )}

        {/* Step 2: Review/Edit Values */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <h3 className="text-green-400 font-medium mb-2">Schritt 2: Werte prüfen</h3>
              <p className="text-zinc-400 text-sm">Überprüfe und korrigiere die erkannten Werte.</p>
            </div>
            
            {/* Product Info */}
            <div className="space-y-3">
              <div>
                <label className="text-zinc-400 text-xs">Produktname *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. Bio Haferflocken"
                  className="w-full px-3 py-2 bg-zinc-800 rounded-lg text-white mt-1"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs">Marke</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  placeholder="z.B. Migros Bio"
                  className="w-full px-3 py-2 bg-zinc-800 rounded-lg text-white mt-1"
                />
              </div>
              <div data-testid="mobile-create-product-barcode-section">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-zinc-400 text-xs">Barcode</label>
                  {isBarcodeLocked && (
                    <div className="flex items-center gap-2" data-testid="mobile-create-product-barcode-lock-controls">
                      <span className="text-[10px] px-2 py-0.5 rounded border border-zinc-600 text-zinc-300" data-testid="mobile-create-product-barcode-locked-badge">
                        fixiert aus Scanner
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setIsBarcodeLocked(false);
                          setTimeout(() => barcodeInputRef.current?.focus(), 30);
                        }}
                        className="text-[10px] px-2 py-1 rounded bg-zinc-700 text-zinc-200"
                        data-testid="mobile-create-product-barcode-unlock-button"
                      >
                        Barcode ändern
                      </button>
                    </div>
                  )}
                </div>
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={formData.barcode}
                  readOnly={isBarcodeLocked}
                  onChange={(e) => {
                    if (!isBarcodeLocked) {
                      setFormData({ ...formData, barcode: e.target.value });
                    }
                  }}
                  placeholder="z.B. 7610200000019"
                  className={`w-full px-3 py-2 rounded-lg text-white mt-1 ${isBarcodeLocked ? 'bg-zinc-900 border border-zinc-700 text-zinc-400' : 'bg-zinc-800'}`}
                  data-testid="mobile-create-product-barcode-input"
                />
                {barcodeConflictHint && (
                  <p className="text-[10px] text-amber-300 mt-1" data-testid="mobile-create-product-barcode-conflict-hint">
                    {barcodeConflictHint}
                  </p>
                )}
              </div>
            </div>
            
            {/* Nutrition Values */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <p className="text-zinc-400 text-xs mb-3">Nährwerte pro 100g</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'calories', label: 'Kalorien', unit: 'kcal' },
                  { key: 'protein', label: 'Protein', unit: 'g' },
                  { key: 'carbs', label: 'Kohlenhydrate', unit: 'g' },
                  { key: 'fat', label: 'Fett', unit: 'g' },
                  { key: 'fiber', label: 'Ballaststoffe', unit: 'g' },
                  { key: 'sugar', label: 'Zucker', unit: 'g' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-zinc-500 text-[10px]">{field.label}</label>
                    <div className="flex items-center gap-1 mt-0.5">
                      <input
                        type="number"
                        value={formData[field.key]}
                        onChange={(e) => setFormData({...formData, [field.key]: parseFloat(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 bg-zinc-700 rounded text-white text-sm"
                      />
                      <span className="text-zinc-500 text-xs">{field.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={() => setStep(3)}
              className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold"
            >
              Weiter zu Produktfoto
            </button>
          </div>
        )}

        {/* Step 3: Product Photo */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <h3 className="text-blue-400 font-medium mb-2">Schritt 3: Produktfoto (optional)</h3>
              <p className="text-zinc-400 text-sm">Füge ein Foto des Produkts hinzu für bessere Wiedererkennung.</p>
            </div>
            
            {!productPreview ? (
              <div 
                onClick={() => productPhotoRef.current?.click()}
                className="bg-zinc-800/70 rounded-2xl p-8 border-2 border-dashed border-zinc-600 cursor-pointer"
              >
                <div className="flex flex-col items-center">
                  <ImageIcon className="w-12 h-12 text-zinc-500 mb-3" />
                  <p className="text-zinc-400 text-sm">Produktfoto hinzufügen</p>
                </div>
              </div>
            ) : (
              <div className="relative">
                <img src={productPreview} alt="Produkt" className="w-full h-48 object-cover rounded-xl" />
                <button
                  onClick={() => { setProductPhoto(null); setProductPreview(null); }}
                  className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
            
            <input
              ref={productPhotoRef}
              type="file"
              accept="image/*"
              onChange={handleProductPhoto}
              className="hidden"
              data-testid="mobile-create-product-step3-file-input"
            />
            
            <div className="flex gap-2">
              <button
                onClick={() => setStep(4)}
                className="flex-1 py-3 bg-zinc-700 rounded-xl text-zinc-300 font-medium"
              >
                Überspringen
              </button>
              <button
                onClick={() => setStep(4)}
                className="flex-1 py-3 bg-yellow-500 rounded-xl text-black font-bold"
              >
                Weiter
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Confirm & Save */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
              <h3 className="text-purple-400 font-medium mb-2">Schritt 4: Bestätigen</h3>
              <p className="text-zinc-400 text-sm">Überprüfe dein neues Produkt und speichere es.</p>
            </div>
            
            {/* Summary */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex gap-4">
                {productPreview && (
                  <img src={productPreview} alt="Produkt" className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <h4 className="text-white font-medium">{formData.name || 'Unbenannt'}</h4>
                  <p className="text-zinc-500 text-sm">{formData.brand || 'Keine Marke'}</p>
                  <p className="text-yellow-400 text-sm mt-1">{Math.round(formData.calories || 0)} kcal/100g</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-zinc-700">
                <div className="text-center">
                  <p className="text-blue-400 font-medium">{formData.protein}g</p>
                  <p className="text-zinc-500 text-xs">Protein</p>
                </div>
                <div className="text-center">
                  <p className="text-orange-400 font-medium">{formData.carbs}g</p>
                  <p className="text-zinc-500 text-xs">Kohlenhy.</p>
                </div>
                <div className="text-center">
                  <p className="text-purple-400 font-medium">{formData.fat}g</p>
                  <p className="text-zinc-500 text-xs">Fett</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={saveProduct}
              disabled={saving || !formData.name.trim()}
              className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Produkt speichern
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Back Button (except step 1) */}
      {step > 1 && (
        <div className="p-4 border-t border-zinc-700">
          <button
            onClick={() => setStep(step - 1)}
            className="w-full py-2 text-zinc-400 text-sm"
          >
            ← Zurück zu Schritt {step - 1}
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateProductWizard;
